// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React from 'react';
import {
  Paper,
  Box,
  Typography
} from '@mui/material';

const QualificationDistributionChart = ({ data }) => {
  if (!data) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Typography variant="h6" gutterBottom>
          Qualification Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Paper>
    );
  }

  const qualified = data.qualified ?? 0;
  const notQualified = data.not_qualified ?? 0;
  const total = qualified + notQualified;
  const maxValue = Math.max(qualified, notQualified, 1);
  const qualifiedHeight = total > 0 ? (qualified / maxValue) * 100 : 0;
  const notQualifiedHeight = total > 0 ? (notQualified / maxValue) * 100 : 0;

  return (
    <Paper sx={{ p: 1.5, height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Qualification Distribution
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Qualified vs Not qualified counts
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 120, display: 'flex', alignItems: 'flex-end', gap: 2, px: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: '100%',
              height: `${qualifiedHeight}%`,
              minHeight: qualified > 0 ? 24 : 4,
              backgroundColor: '#4caf50',
              borderRadius: '4px 4px 0 0',
              transition: 'height 0.3s ease'
            }}
            title={`Qualified: ${qualified} records`}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Qualified
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="success.main">
            {qualified}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: '100%',
              height: `${notQualifiedHeight}%`,
              minHeight: notQualified > 0 ? 24 : 4,
              backgroundColor: '#f44336',
              borderRadius: '4px 4px 0 0',
              transition: 'height 0.3s ease'
            }}
            title={`Not qualified: ${notQualified} records`}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Not qualified
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="error.main">
            {notQualified}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1.5, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Total: <strong>{total}</strong> records
        </Typography>
      </Box>
    </Paper>
  );
};

export default QualificationDistributionChart;
